import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Alert,
  DatePicker,
  Statistic,
  Row,
  Col,
  InputNumber,
  Divider,
  Checkbox,
  Tooltip,
  Drawer,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  DollarOutlined,
  ReloadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  EyeOutlined,
  MailOutlined,
  MessageOutlined,
  BellOutlined,
  PrinterOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const Billing = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [isInvoiceDetailVisible, setIsInvoiceDetailVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [dateRange, setDateRange] = useState(null);

  const dateRangeKey = dateRange
    ? `${dateRange[0]?.format('YYYY-MM-DD')}-${dateRange[1]?.format('YYYY-MM-DD')}`
    : 'default';

  const [lineItems, setLineItems] = useState([]);
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [barcodeResult, setBarcodeResult] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const queryClient = useQueryClient();

  const {
    data: invoices = [],
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['invoices', dateRangeKey, searchText],
    async () => {
      try {
        const params = new URLSearchParams();
        if (dateRange && dateRange.length === 2) {
          params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
          params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
        }
        if (searchText) {
          params.append('search', searchText);
        }
        params.append('per_page', '100');

        const response = await api.get(`/admin/billing/invoices?${params.toString()}`);
        const result = response.data?.data;

        if (result?.data && Array.isArray(result.data)) {
          return result.data;
        }
        if (result?.data && Array.isArray(result.data.data)) {
          return result.data.data;
        }
        if (Array.isArray(result)) {
          return result;
        }
        if (Array.isArray(response.data)) {
          return response.data;
        }

        return [];
      } catch (err) {
        console.error('Failed to fetch invoices:', err);
        if (err.response?.status === 401) {
          window.location.href = '/login';
        }
        return [];
      }
    },
    { retry: 1, staleTime: 2 * 60 * 1000 },
  );

  useQuery(
    'invoice-stats',
    async () => {
      try {
        const response = await api.get('/admin/billing/stats');
        return response.data?.data ?? {};
      } catch (error) {
        console.error('Failed to fetch invoice stats:', error);
        return {};
      }
    },
    { retry: 1 },
  );

  const { data: products = [] } = useQuery(
    'products-services',
    async () => {
      try {
        const response = await api.get('/admin/products-services?per_page=1000');
        return response.data?.data?.data ?? response.data?.data ?? response.data ?? [];
      } catch (error) {
        console.error('Failed to fetch products:', error);
        return [];
      }
    },
    { retry: 1 },
  );

  const { data: customers = [] } = useQuery(
    'billing-customers',
    async () => {
      try {
        const response = await api.get('/admin/users/list');
        return response.data?.data ?? response.data?.users ?? response.data ?? [];
      } catch (error) {
        console.error('Failed to fetch customers:', error);
        return [];
      }
    },
    { retry: 1 },
  );

  const createMutation = useMutation(
    async (values) => {
      const calculatedSubtotal = lineItems.reduce((sum, item) => sum + (item.total || 0), 0);

      const payload = {
        customer_id: values.customer_id || null,
        customer_name: values.customer_name,
        customer_email: values.customer_email,
        customer_phone: values.customer_phone || null,
        type: values.type || 'product',
        billing_type: values.billing_type || 'standard',
        description: values.description || 'Invoice description',
        subtotal: calculatedSubtotal || values.subtotal || 0,
        total_amount: calculatedSubtotal || values.subtotal || 0,
        tax_amount: values.tax_amount || 0,
        vat_rate: values.vat_rate || null,
        vat_amount: values.vat_amount || 0,
        status: values.status || 'draft',
        issue_date: values.issue_date?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
        due_date: values.due_date?.format('YYYY-MM-DD') || null,
        payment_method: values.payment_method || null,
        line_items: lineItems.length > 0 ? lineItems : null,
        affect_stock: values.affect_stock ?? true,
      };

      const response = await api.post('/admin/billing/invoices', payload);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Invoice created successfully');
        setIsModalVisible(false);
        form.resetFields();
        setLineItems([]);
        queryClient.invalidateQueries(['invoices', dateRangeKey]);
        queryClient.invalidateQueries('invoice-stats');
      },
      onError: (error) => {
        const errorMsg =
          error.response?.data?.message || error.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(', ')
            : error.message;
        message.error('Failed to create invoice: ' + errorMsg);
      },
    },
  );

  const updateStatusMutation = useMutation(
    async ({ invoiceId, paymentMethod }) => {
      const response = await api.post(`/admin/billing/invoices/${invoiceId}/mark-paid`, {
        payment_method: paymentMethod,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Invoice marked as paid successfully');
        queryClient.invalidateQueries('invoices');
      },
      onError: (error) => {
        message.error(
          'Failed to update invoice: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  const deleteMutation = useMutation(
    async (invoiceId) => {
      await api.delete(`/admin/billing/invoices/${invoiceId}`);
    },
    {
      onSuccess: () => {
        message.success('Invoice deleted successfully');
        queryClient.invalidateQueries('invoices');
      },
      onError: (error) => {
        message.error(
          'Failed to delete invoice: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  const sendEmailMutation = useMutation(
    async ({ invoiceId, email }) => {
      const response = await api.post(`/admin/billing/invoices/${invoiceId}/share`, { email });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Invoice sent via email successfully');
        setIsEmailModalVisible(false);
        emailForm.resetFields();
        queryClient.invalidateQueries('invoices');
      },
      onError: (error) => {
        message.error('Failed to send email: ' + (error.response?.data?.message || error.message));
      },
    },
  );

  const sendSmsMutation = useMutation(
    async (invoiceId) => {
      const response = await api.post(`/admin/billing/invoices/${invoiceId}/send-sms`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Invoice link sent via SMS successfully');
        queryClient.invalidateQueries('invoices');
      },
      onError: (error) => {
        message.error('Failed to send SMS: ' + (error.response?.data?.message || error.message));
      },
    },
  );

  const sendPushMutation = useMutation(
    async (invoiceId) => {
      const response = await api.post(`/admin/billing/invoices/${invoiceId}/send-push`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Push notification sent successfully');
        queryClient.invalidateQueries('invoices');
      },
      onError: (error) => {
        message.error(
          'Failed to send push notification: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  const sendWhatsAppMutation = useMutation(
    async (invoiceId) => {
      const response = await api.post(`/admin/billing/invoices/${invoiceId}/send-whatsapp`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        const msg = data?.data?.simulated
          ? 'WhatsApp message logged (configure TWILIO_ACCOUNT_SID to enable live sending)'
          : 'Invoice sent via WhatsApp successfully';
        message.success(msg);
        queryClient.invalidateQueries('invoices');
      },
      onError: (error) => {
        message.error(
          'Failed to send WhatsApp: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  const sendViberMutation = useMutation(
    async (invoiceId) => {
      const response = await api.post(`/admin/billing/invoices/${invoiceId}/send-viber`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        const msg = data?.data?.simulated
          ? 'Viber message logged (configure VIBER_API_TOKEN to enable live sending)'
          : 'Invoice sent via Viber successfully';
        message.success(msg);
        queryClient.invalidateQueries('invoices');
      },
      onError: (error) => {
        message.error('Failed to send Viber: ' + (error.response?.data?.message || error.message));
      },
    },
  );

  const sendMessengerMutation = useMutation(
    async (invoiceId) => {
      const response = await api.post(`/admin/billing/invoices/${invoiceId}/send-messenger`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        const msg = data?.data?.simulated
          ? 'Messenger message logged (configure MESSENGER_PAGE_ACCESS_TOKEN to enable live sending)'
          : 'Invoice sent via Messenger successfully';
        message.success(msg);
        queryClient.invalidateQueries('invoices');
      },
      onError: (error) => {
        message.error(
          'Failed to send Messenger: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  const handleViewPdf = async (invoice) => {
    try {
      const response = await api.get(`/admin/billing/invoices/${invoice.id}/pdf/download`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      setPreviewUrl(blobUrl);
      setSelectedInvoice(invoice);
      setIsPreviewModalVisible(true);
    } catch (error) {
      message.error('Failed to load PDF preview');
    }
  };

  const handleDownload = async (invoice) => {
    try {
      const response = await api.get(`/admin/billing/invoices/${invoice.id}/pdf/download`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `invoice-${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      message.success('Download started');
    } catch (error) {
      message.error('Failed to download PDF');
    }
  };

  const handlePrint = async (invoice) => {
    try {
      const response = await api.get(`/admin/billing/invoices/${invoice.id}/pdf/download`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      const newWindow = window.open(blobUrl, '_blank');
      if (newWindow) {
        newWindow.onload = () => {
          newWindow.print();
          URL.revokeObjectURL(blobUrl);
        };
      }
    } catch (error) {
      message.error('Failed to load PDF for printing');
    }
  };

  const openInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setIsInvoiceDetailVisible(true);
  };

  const openEmailModal = (invoice) => {
    setSelectedInvoice(invoice);
    emailForm.setFieldsValue({ email: invoice.customer_email });
    setIsEmailModalVisible(true);
  };

  const confirmSendSms = (invoice) => {
    Modal.confirm({
      title: 'Send via SMS',
      icon: <MessageOutlined />,
      content: (
        <div>
          <p>Send invoice #{invoice.invoice_number} to:</p>
          <p style={{ fontWeight: 'bold' }}>{invoice.customer_phone || 'No phone number'}</p>
          <Alert
            message="SMS will include the invoice link and amount"
            type="info"
            showIcon
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: 'Send SMS',
      cancelText: 'Cancel',
      onOk: () => {
        if (!invoice.customer_phone) {
          message.error('Customer phone number not available');
          return Promise.reject();
        }
        sendSmsMutation.mutate(invoice.id);
      },
    });
  };

  const confirmSendPush = (invoice) => {
    Modal.confirm({
      title: 'Send Push Notification',
      icon: <BellOutlined />,
      content: (
        <div>
          <p>Send push notification for invoice #{invoice.invoice_number}</p>
          <p>
            To:{' '}
            <strong>
              {invoice.customer?.name || invoice.customer?.full_name || invoice.customer_name}
            </strong>
          </p>
          <Alert
            message="Customer will receive a notification with the invoice link"
            type="info"
            showIcon
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: 'Send Notification',
      cancelText: 'Cancel',
      onOk: () => sendPushMutation.mutate(invoice.id),
    });
  };

  const confirmSendWhatsApp = (invoice) => {
    Modal.confirm({
      title: 'Send via WhatsApp',
      icon: <MessageOutlined style={{ color: '#25D366' }} />,
      content: (
        <div>
          <p>Send invoice #{invoice.invoice_number} to:</p>
          <p style={{ fontWeight: 'bold' }}>{invoice.customer_phone || 'No phone number'}</p>
          <Alert
            message="WhatsApp message will include the invoice link and amount"
            type="info"
            showIcon
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: 'Send WhatsApp',
      cancelText: 'Cancel',
      onOk: () => {
        if (!invoice.customer_phone) {
          message.error('Customer phone number not available');
          return Promise.reject();
        }
        sendWhatsAppMutation.mutate(invoice.id);
      },
    });
  };

  const confirmSendViber = (invoice) => {
    Modal.confirm({
      title: 'Send via Viber',
      icon: <PhoneOutlined style={{ color: '#7360F2' }} />,
      content: (
        <div>
          <p>Send invoice #{invoice.invoice_number} to:</p>
          <p>
            <strong>
              {invoice.customer?.name || invoice.customer?.full_name || invoice.customer_name}
            </strong>
          </p>
          <Alert
            message="Viber message will include the invoice link and amount. Customer must have Viber linked to their account."
            type="info"
            showIcon
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: 'Send Viber',
      cancelText: 'Cancel',
      onOk: () => sendViberMutation.mutate(invoice.id),
    });
  };

  const confirmSendMessenger = (invoice) => {
    Modal.confirm({
      title: 'Send via Messenger',
      icon: <CommentOutlined style={{ color: '#0084FF' }} />,
      content: (
        <div>
          <p>Send invoice #{invoice.invoice_number} to:</p>
          <p>
            <strong>
              {invoice.customer?.name || invoice.customer?.full_name || invoice.customer_name}
            </strong>
          </p>
          <Alert
            message="Messenger message will include the invoice link and amount. Customer must have Messenger linked to their account."
            type="info"
            showIcon
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: 'Send Messenger',
      cancelText: 'Cancel',
      onOk: () => sendMessengerMutation.mutate(invoice.id),
    });
  };

  const confirmPayment = (invoice) => {
    if (invoice.status === 'paid') {
      return;
    }
    setPaymentMethod('');
    Modal.confirm({
      title: 'Process Payment',
      icon: <DollarOutlined />,
      content: (
        <div>
          <p>Mark invoice #{invoice.invoice_number} as paid?</p>
          <p>
            Amount:{' '}
            <strong style={{ color: '#52c41a', fontSize: 18 }}>
              Rs. {(parseFloat(invoice.net_amount || invoice.total_amount) || 0).toFixed(2)}
            </strong>
            {parseFloat(invoice.refunded_amount || 0) > 0 && (
              <Text type="danger" style={{ marginLeft: 8, fontSize: 12 }}>
                (Refunded: Rs. {parseFloat(invoice.refunded_amount).toFixed(2)})
              </Text>
            )}
          </p>
          <div style={{ marginTop: 16 }}>
            <Select
              placeholder="Select payment method"
              style={{ width: '100%' }}
              onChange={(value) => setPaymentMethod(value)}
            >
              <Option value="cash">Cash</Option>
              <Option value="bank_transfer">Bank Transfer</Option>
              <Option value="card">Card</Option>
              <Option value="online">Online Payment</Option>
              <Option value="mobile_banking">Mobile Banking</Option>
              <Option value="other">Other</Option>
            </Select>
          </div>
        </div>
      ),
      okText: 'Confirm Payment',
      cancelText: 'Cancel',
      onOk: () => {
        if (!paymentMethod) {
          message.error('Please select a payment method');
          return false;
        }
        return updateStatusMutation.mutateAsync({ invoiceId: invoice.id, paymentMethod });
      },
    });
  };

  const confirmDelete = (invoice) => {
    Modal.confirm({
      title: 'Delete Invoice',
      icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>Are you sure you want to delete invoice #{invoice.invoice_number}?</p>
          <Alert message="This action cannot be undone" type="warning" showIcon />
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => deleteMutation.mutate(invoice.id),
    });
  };

  const searchByBarcode = async (barcode) => {
    if (!barcode) return;
    try {
      const response = await api.get('/admin/products-services/barcode/' + barcode);
      if (response.data?.data) {
        setBarcodeResult(response.data.data);
        message.success(`Found: ${response.data.data.name}`);
      } else {
        setBarcodeResult(null);
        message.warning('Product not found for this barcode');
      }
    } catch (error) {
      setBarcodeResult(null);
      message.warning('Product not found for this barcode');
    }
  };

  const addFromBarcode = () => {
    if (barcodeResult) {
      const effectivePrice = parseFloat(barcodeResult.sale_price ?? barcodeResult.price) || 0;
      const newItem = {
        name: barcodeResult.name,
        type: barcodeResult.type,
        product_id: barcodeResult.id,
        quantity: 1,
        unit_price: effectivePrice,
        total: effectivePrice,
        category: barcodeResult.category?.name,
        unit: barcodeResult.unit?.name,
        stock: barcodeResult.stock_quantity,
        sku: barcodeResult.sku,
        barcode: barcodeResult.barcode,
      };
      setLineItems([...lineItems, newItem]);
      setBarcodeResult(null);
      setBarcodeSearch('');
    }
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        name: '',
        type: 'product',
        product_id: null,
        quantity: 1,
        unit_price: 0,
        total: 0,
        category: null,
        unit: null,
        stock: null,
      },
    ]);
  };

  const removeLineItem = (index) => {
    const newItems = [...lineItems];
    newItems.splice(index, 1);
    setLineItems(newItems);
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].unit_price || 0);
    }
    setLineItems(newItems);
  };

  const calculateSubtotal = () => lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
  const calculateProductSubtotal = () =>
    lineItems.filter((i) => i.type === 'product').reduce((sum, item) => sum + (item.total || 0), 0);
  const calculateServiceSubtotal = () =>
    lineItems.filter((i) => i.type === 'service').reduce((sum, item) => sum + (item.total || 0), 0);

  const manualBillingType = Form.useWatch('billing_type', form) || 'standard';
  const manualVatRate = 13;
  const manualVatOnServices = manualBillingType === 'vat_all';
  const manualTaxableBase = manualVatOnServices ? calculateSubtotal() : calculateProductSubtotal();
  const manualProductTaxable =
    manualBillingType === 'vat' || manualBillingType === 'vat_all'
      ? manualTaxableBase / (1 + manualVatRate / 100)
      : calculateProductSubtotal();
  const manualVatAmount =
    manualBillingType === 'vat' || manualBillingType === 'vat_all'
      ? manualTaxableBase - manualTaxableBase / (1 + manualVatRate / 100)
      : 0;

  const totalInvoices = invoices.length;
  const paidInvoices = invoices.filter((inv) => inv.status === 'paid').length;
  const pendingInvoices = invoices.filter((inv) => inv.status === 'pending').length;
  const totalRefunded = invoices.reduce((sum, inv) => sum + Number(inv.refunded_amount || 0), 0);
  const totalAmount = invoices.reduce(
    (sum, inv) => sum + Number(inv.net_amount || inv.total_amount || 0),
    0,
  );

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      sorter: (a, b) => (a.invoice_number || '').localeCompare(b.invoice_number || ''),
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.billing_type?.toUpperCase()}{' '}
            {record.type === 'service' ? '(Service)' : '(Product)'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      sorter: (a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''),
      render: (customer_name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>
            {record.customer?.name || record.customer?.full_name || customer_name || 'N/A'}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.customer?.email || record.customer_email || ''}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 180,
      sorter: (a, b) => (parseFloat(a.total_amount) || 0) - (parseFloat(b.total_amount) || 0),
      render: (total_amount, record) => {
        const refunded = parseFloat(record.refunded_amount || 0);
        if (refunded > 0) {
          const net = parseFloat(record.net_amount || record.total_amount || 0);
          return (
            <Space direction="vertical" size={0}>
              <Text delete type="secondary" style={{ fontSize: 12 }}>
                Rs. {(parseFloat(total_amount) || 0).toFixed(2)}
              </Text>
              <Text strong style={{ color: '#52c41a', fontSize: 15 }}>
                Rs. {net.toFixed(2)}
              </Text>
              <Tag color="red" style={{ fontSize: 10, lineHeight: '16px' }}>
                Refunded: Rs. {refunded.toFixed(2)}
              </Tag>
            </Space>
          );
        }
        return (
          <Text strong style={{ color: '#52c41a', fontSize: 15 }}>
            Rs. {(parseFloat(total_amount) || 0).toFixed(2)}
          </Text>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (status) => {
        const colorMap = {
          paid: 'green',
          pending: 'orange',
          overdue: 'red',
          cancelled: 'red',
          draft: 'default',
        };
        return <Tag color={colorMap[status] || 'default'}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Date',
      dataIndex: 'issue_date',
      key: 'issue_date',
      width: 100,
      sorter: (a, b) => new Date(a.issue_date) - new Date(b.issue_date),
      render: (date) => dayjs(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 450,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4} wrap>
          <Tooltip title="View PDF">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewPdf(record)}
            />
          </Tooltip>
          <Tooltip title="Download">
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Tooltip title="Print">
            <Button icon={<PrinterOutlined />} size="small" onClick={() => handlePrint(record)} />
          </Tooltip>
          <Tooltip title={record.status === 'paid' ? 'Paid' : 'Mark as Paid'}>
            <Button
              icon={<DollarOutlined />}
              size="small"
              style={
                record.status === 'paid'
                  ? { background: '#52c41a', color: '#fff', borderColor: '#52c41a' }
                  : {}
              }
              onClick={() => confirmPayment(record)}
            >
              {record.status === 'paid' ? 'Paid' : 'Pay'}
            </Button>
          </Tooltip>
          <Tooltip title="Email">
            <Button icon={<MailOutlined />} size="small" onClick={() => openEmailModal(record)} />
          </Tooltip>
          <Tooltip title="SMS">
            <Button
              icon={<MessageOutlined />}
              size="small"
              onClick={() => confirmSendSms(record)}
            />
          </Tooltip>
          <Tooltip title="Push">
            <Button icon={<BellOutlined />} size="small" onClick={() => confirmSendPush(record)} />
          </Tooltip>
          <Tooltip title="WhatsApp">
            <Button
              icon={<MessageOutlined />}
              size="small"
              style={{ color: '#25D366' }}
              onClick={() => confirmSendWhatsApp(record)}
            />
          </Tooltip>
          <Tooltip title="Viber">
            <Button
              icon={<PhoneOutlined />}
              size="small"
              style={{ color: '#7360F2' }}
              onClick={() => confirmSendViber(record)}
            />
          </Tooltip>
          <Tooltip title="Messenger">
            <Button
              icon={<CommentOutlined />}
              size="small"
              style={{ color: '#0084FF' }}
              onClick={() => confirmSendMessenger(record)}
            />
          </Tooltip>
          <Tooltip title="Details">
            <Button
              icon={<InfoCircleOutlined />}
              size="small"
              onClick={() => openInvoiceDetail(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => confirmDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {error && (
        <Alert
          message="Error Loading Invoices"
          description="Failed to load invoice data. Please try again."
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      <Title level={2}>
        <FileTextOutlined /> Invoice Management
      </Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Total Invoices"
              value={totalInvoices}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Paid Invoices"
              value={paidInvoices}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Pending Invoices"
              value={pendingInvoices}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Revenue (After Refunds)"
              value={totalAmount}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#722ed1' }}
              suffix="NPR"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Total Refunded"
              value={totalRefunded}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#ff4d4f' }}
              suffix="NPR"
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <Input.Search
            placeholder="Search by invoice #, customer name, or email..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(value) => setSearchText(value)}
            style={{ width: 320 }}
          />
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
              Create Invoice
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
              Refresh
            </Button>
          </Space>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
          />
        </div>

        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${total} invoices`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Create Invoice Modal */}
      <Modal
        title="Create New Invoice"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setLineItems([]);
        }}
        footer={null}
        width={900}
      >
        <Form form={form} layout="vertical" onFinish={(values) => createMutation.mutate(values)}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="customer_id" label="Existing Customer (optional)">
                <Select
                  allowClear
                  showSearch
                  placeholder="Select a customer, or enter name/email below for a manual invoice"
                  filterOption={(input, option) =>
                    String(option.children || '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value, option) => {
                    if (value) {
                      form.setFieldsValue({
                        customer_name: option?.name || option?.full_name || '',
                        customer_email: option?.email || '',
                        customer_phone: option?.phone_number || '',
                      });
                    }
                  }}
                >
                  {(Array.isArray(customers) ? customers : []).map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name || c.full_name} {c.phone_number ? `(${c.phone_number})` : ''} -{' '}
                      {c.email}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="customer_name"
                label="Customer Name"
                rules={[{ required: true, message: 'Customer name required' }]}
              >
                <Input placeholder="Enter customer name" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="customer_email" label="Email">
                <Input placeholder="customer@example.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="customer_phone"
                label="Phone"
                rules={[{ pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' }]}
              >
                <Input
                  type="tel"
                  onKeyPress={(e) => e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()}
                  placeholder="+977-98XXXXXXXX"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="billing_type" label="Billing Type" initialValue="standard">
                <Select>
                  <Option value="standard">Standard</Option>
                  <Option value="vat">VAT (13% Products)</Option>
                  <Option value="vat_all">VAT (13% Products + Services)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status" initialValue="draft">
                <Select>
                  <Option value="draft">Draft</Option>
                  <Option value="pending">Pending</Option>
                  <Option value="paid">Paid</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="due_date" label="Due Date">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Quick Add (Barcode Scanner)</Divider>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Card size="small" style={{ background: '#fafafa' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Input
                      placeholder="Scan or enter barcode number..."
                      value={barcodeSearch}
                      onChange={(e) => setBarcodeSearch(e.target.value)}
                      onPressEnter={() => searchByBarcode(barcodeSearch)}
                      prefix={<span style={{ color: '#999' }}>Barcode:</span>}
                      suffix={
                        <Button type="link" onClick={() => searchByBarcode(barcodeSearch)}>
                          Search
                        </Button>
                      }
                    />
                    {barcodeResult && (
                      <div
                        style={{ marginTop: 8, padding: 8, background: '#e6f7ff', borderRadius: 4 }}
                      >
                        <Row align="middle">
                          <Col span={14}>
                            <p style={{ margin: 0 }}>
                              <strong>{barcodeResult.name}</strong>
                            </p>
                            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                              Price: Rs.{' '}
                              {Number(
                                (barcodeResult?.sale_price ?? barcodeResult?.price) || 0,
                              ).toFixed(2)}{' '}
                              | Stock: {barcodeResult.stock_quantity || 'N/A'}
                            </p>
                          </Col>
                          <Col span={10} style={{ textAlign: 'right' }}>
                            <Button type="primary" size="small" onClick={addFromBarcode}>
                              Add to Invoice
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Scan barcode or search products to add items quickly
                    </Text>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Divider>Line Items</Divider>
          <Row
            gutter={8}
            style={{
              marginBottom: 8,
              fontWeight: 'bold',
              color: '#666',
              fontSize: 12,
              paddingLeft: 8,
            }}
          >
            <Col span={2}>Type</Col>
            <Col span={7}>Product/Service</Col>
            <Col span={3}>Category</Col>
            <Col span={2}>Qty</Col>
            <Col span={4}>Unit Price</Col>
            <Col span={4}>Total</Col>
            <Col span={2}>Stock</Col>
            <Col span={2}></Col>
          </Row>

          {lineItems.map((item, index) => {
            const filteredProducts = products.filter((p) => !item.type || p.type === item.type);
            return (
              <Card key={index} size="small" style={{ marginBottom: 8, background: '#fafafa' }}>
                <Row gutter={8} align="middle">
                  <Col span={2}>
                    <Select
                      value={item.type}
                      onChange={(value) => {
                        updateLineItem(index, 'type', value);
                        updateLineItem(index, 'name', '');
                        updateLineItem(index, 'product_id', null);
                      }}
                      style={{ width: '100%' }}
                    >
                      <Option value="product">Product</Option>
                      <Option value="service">Service</Option>
                    </Select>
                  </Col>
                  <Col span={7}>
                    <Select
                      showSearch
                      placeholder="Search..."
                      value={
                        item.product_id || (item.name && !item.product_id ? item.name : undefined)
                      }
                      onChange={(value, _option) => {
                        if (typeof value === 'number') {
                          const product = products.find((p) => p.id === value);
                          updateLineItem(index, 'product_id', value);
                          updateLineItem(index, 'name', product?.name || '');
                          updateLineItem(
                            index,
                            'unit_price',
                            parseFloat((product?.sale_price ?? product?.price) || 0),
                          );
                          updateLineItem(index, 'category', product?.category?.name);
                          updateLineItem(index, 'stock', product?.stock_quantity);
                        } else {
                          updateLineItem(index, 'product_id', null);
                          updateLineItem(index, 'name', value);
                        }
                      }}
                      filterOption={(input, option) =>
                        option.children.toLowerCase().includes(input.toLowerCase())
                      }
                      style={{ width: '100%' }}
                    >
                      {filteredProducts.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {p.name} - Rs. {Number((p?.sale_price ?? p?.price) || 0).toFixed(2)}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={3}>
                    <Input
                      value={item.category || ''}
                      onChange={(e) => updateLineItem(index, 'category', e.target.value)}
                      placeholder="Category"
                    />
                  </Col>
                  <Col span={2}>
                    <InputNumber
                      value={item.quantity}
                      onChange={(value) => updateLineItem(index, 'quantity', value || 0)}
                      min={1}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={4}>
                    <InputNumber
                      value={item.unit_price}
                      onChange={(value) => updateLineItem(index, 'unit_price', value || 0)}
                      min={0}
                      precision={2}
                      prefix="Rs."
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={4}>
                    <Input value={Number(item?.total || 0).toFixed(2)} disabled prefix="Rs." />
                  </Col>
                  <Col span={2}>
                    {item.type === 'product' && item.stock !== undefined && (
                      <Tag color={item.stock > 0 ? 'green' : 'red'}>{item.stock}</Tag>
                    )}
                  </Col>
                  <Col span={2}>
                    <Button
                      type="text"
                      danger
                      onClick={() => removeLineItem(index)}
                      icon={<DeleteOutlined />}
                    />
                  </Col>
                </Row>
              </Card>
            );
          })}

          <Space style={{ marginBottom: 16 }}>
            <Button type="dashed" onClick={addLineItem}>
              + Add Line Item
            </Button>
          </Space>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Form.Item name="affect_stock" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Update stock inventory</Checkbox>
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Uncheck for manual invoices
              </Text>
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'right' }}>
                <div>
                  <Text>
                    {manualBillingType === 'standard' ? 'Product Subtotal:' : 'Taxable Amount:'}
                  </Text>{' '}
                  <Text strong>Rs. {manualProductTaxable.toFixed(2)}</Text>
                </div>
                {manualBillingType !== 'vat_all' && (
                  <div>
                    <Text>
                      {manualBillingType === 'vat'
                        ? 'Tax Exempt Amount (Services):'
                        : 'Service Subtotal:'}
                    </Text>{' '}
                    <Text strong>Rs. {calculateServiceSubtotal().toFixed(2)}</Text>
                  </div>
                )}
                {(manualBillingType === 'vat' || manualBillingType === 'vat_all') &&
                  manualVatAmount > 0 && (
                    <div>
                      <Text>VAT ({manualVatRate}%):</Text>{' '}
                      <Text strong>Rs. {manualVatAmount.toFixed(2)}</Text>
                    </div>
                  )}
                <div>
                  <Text strong style={{ fontSize: 16 }}>
                    Total:
                  </Text>{' '}
                  <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                    Rs. {calculateSubtotal().toFixed(2)}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Invoice description..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isLoading}
                icon={<PlusOutlined />}
              >
                Create Invoice
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                  setLineItems([]);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Email Modal */}
      <Modal
        title={
          <>
            <MailOutlined /> Send Invoice via Email
          </>
        }
        open={isEmailModalVisible}
        onCancel={() => {
          setIsEmailModalVisible(false);
          emailForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        {selectedInvoice && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: '#f0f5ff',
              borderRadius: 8,
              border: '1px solid #d9d9d9',
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Invoice
                </Text>
                <div>
                  <Text strong>#{selectedInvoice.invoice_number}</Text>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Amount
                </Text>
                <div>
                  <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                    Rs. {(parseFloat(selectedInvoice.total_amount) || 0).toFixed(2)}
                  </Text>
                </div>
              </Col>
            </Row>
            <Divider style={{ margin: '12px 0' }} />
            <Space>
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleViewPdf(selectedInvoice)}
                style={{ padding: 0 }}
              >
                Preview PDF
              </Button>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(selectedInvoice)}
                style={{ padding: 0 }}
              >
                Download
              </Button>
            </Space>
          </div>
        )}

        <Form
          form={emailForm}
          layout="vertical"
          onFinish={(values) => {
            if (selectedInvoice)
              sendEmailMutation.mutate({ invoiceId: selectedInvoice.id, email: values.email });
          }}
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter email address' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="customer@example.com" prefix={<MailOutlined />} />
          </Form.Item>

          <Alert
            message="The invoice PDF will be attached and sent to the email address above"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={sendEmailMutation.isLoading}
                icon={<MailOutlined />}
              >
                Send Invoice
              </Button>
              <Button onClick={() => setIsEmailModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        title={
          <>
            <EyeOutlined /> Invoice Preview - #{selectedInvoice?.invoice_number}
          </>
        }
        open={isPreviewModalVisible}
        onCancel={() => {
          setIsPreviewModalVisible(false);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }}
        footer={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => selectedInvoice && handleDownload(selectedInvoice)}
            >
              Download
            </Button>
            <Button
              icon={<PrinterOutlined />}
              onClick={() => selectedInvoice && handlePrint(selectedInvoice)}
            >
              Print
            </Button>
            <Button
              icon={<MailOutlined />}
              onClick={() => {
                setIsPreviewModalVisible(false);
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }
                selectedInvoice && openEmailModal(selectedInvoice);
              }}
            >
              Send Email
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setIsPreviewModalVisible(false);
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }
              }}
            >
              Close
            </Button>
          </Space>
        }
        width={800}
        height={600}
      >
        {previewUrl && (
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '500px', border: 'none' }}
            title="Invoice Preview"
          />
        )}
      </Modal>

      {/* Invoice Details Drawer */}
      <Drawer
        title={
          <>
            <InfoCircleOutlined /> Invoice Details
          </>
        }
        placement="right"
        width={600}
        open={isInvoiceDetailVisible}
        onClose={() => setIsInvoiceDetailVisible(false)}
        extra={
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={() => {
                setIsInvoiceDetailVisible(false);
                selectedInvoice && handleViewPdf(selectedInvoice);
              }}
            >
              View PDF
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => selectedInvoice && handleDownload(selectedInvoice)}
            >
              Download
            </Button>
          </Space>
        }
      >
        {selectedInvoice && (
          <div>
            <Card title="Invoice Information" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text type="secondary">Invoice Number:</Text>
                </Col>
                <Col span={12}>
                  <Text strong>{selectedInvoice.invoice_number}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Status:</Text>
                </Col>
                <Col span={12}>
                  <Tag color={selectedInvoice.status === 'paid' ? 'green' : 'orange'}>
                    {selectedInvoice.status?.toUpperCase()}
                  </Tag>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Billing Type:</Text>
                </Col>
                <Col span={12}>
                  <Text>{selectedInvoice.billing_type?.toUpperCase()}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Issue Date:</Text>
                </Col>
                <Col span={12}>
                  <Text>{dayjs(selectedInvoice.issue_date).format('MMMM DD, YYYY')}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Due Date:</Text>
                </Col>
                <Col span={12}>
                  <Text>
                    {selectedInvoice.due_date
                      ? dayjs(selectedInvoice.due_date).format('MMMM DD, YYYY')
                      : 'N/A'}
                  </Text>
                </Col>
              </Row>
            </Card>

            <Card title="Customer Information" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text type="secondary">Name:</Text>
                </Col>
                <Col span={12}>
                  <Text strong>
                    {selectedInvoice.customer?.name ||
                      selectedInvoice.customer?.full_name ||
                      selectedInvoice.customer_name}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Email:</Text>
                </Col>
                <Col span={12}>
                  <Text>{selectedInvoice.customer_email}</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Phone:</Text>
                </Col>
                <Col span={12}>
                  <Text>{selectedInvoice.customer_phone || 'N/A'}</Text>
                </Col>
              </Row>
            </Card>

            <Card title="Amount Details" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text type="secondary">Subtotal:</Text>
                </Col>
                <Col span={12}>
                  <Text>Rs. {(parseFloat(selectedInvoice.subtotal) || 0).toFixed(2)}</Text>
                </Col>
                {selectedInvoice.vat_amount > 0 && (
                  <>
                    <Col span={12}>
                      <Text type="secondary">VAT ({selectedInvoice.vat_rate}%):</Text>
                    </Col>
                    <Col span={12}>
                      <Text>Rs. {(parseFloat(selectedInvoice.vat_amount) || 0).toFixed(2)}</Text>
                    </Col>
                  </>
                )}
                <Col span={12}>
                  <Text type="secondary">Total Amount:</Text>
                </Col>
                <Col span={12}>
                  <Text strong style={{ color: '#52c41a', fontSize: 18 }}>
                    Rs. {(parseFloat(selectedInvoice.total_amount) || 0).toFixed(2)}
                  </Text>
                </Col>
                {parseFloat(selectedInvoice.refunded_amount || 0) > 0 && (
                  <>
                    <Col span={12}>
                      <Text type="danger">Refunded Amount:</Text>
                    </Col>
                    <Col span={12}>
                      <Text type="danger">
                        -Rs. {(parseFloat(selectedInvoice.refunded_amount) || 0).toFixed(2)}
                      </Text>
                    </Col>
                    <Col span={12}>
                      <Text strong>Net Amount:</Text>
                    </Col>
                    <Col span={12}>
                      <Text strong style={{ color: '#52c41a', fontSize: 18 }}>
                        Rs.{' '}
                        {(
                          parseFloat(selectedInvoice.net_amount || selectedInvoice.total_amount) ||
                          0
                        ).toFixed(2)}
                      </Text>
                    </Col>
                  </>
                )}
              </Row>
            </Card>

            <Card title="Actions" size="small">
              <Space wrap>
                <Button
                  type="primary"
                  icon={<DollarOutlined />}
                  onClick={() => confirmPayment(selectedInvoice)}
                  disabled={selectedInvoice.status === 'paid'}
                >
                  {selectedInvoice.status === 'paid' ? 'Already Paid' : 'Mark as Paid'}
                </Button>
                <Button icon={<MailOutlined />} onClick={() => openEmailModal(selectedInvoice)}>
                  Send Email
                </Button>
                <Button icon={<MessageOutlined />} onClick={() => confirmSendSms(selectedInvoice)}>
                  Send SMS
                </Button>
                <Button icon={<BellOutlined />} onClick={() => confirmSendPush(selectedInvoice)}>
                  Push Notification
                </Button>
                <Button
                  icon={<MessageOutlined />}
                  style={{ color: '#25D366' }}
                  onClick={() => confirmSendWhatsApp(selectedInvoice)}
                >
                  WhatsApp
                </Button>
                <Button
                  icon={<PhoneOutlined />}
                  style={{ color: '#7360F2' }}
                  onClick={() => confirmSendViber(selectedInvoice)}
                >
                  Viber
                </Button>
                <Button
                  icon={<CommentOutlined />}
                  style={{ color: '#0084FF' }}
                  onClick={() => confirmSendMessenger(selectedInvoice)}
                >
                  Messenger
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => confirmDelete(selectedInvoice)}
                >
                  Delete
                </Button>
              </Space>
            </Card>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Billing;
